import { useAuth } from '@/services/auth';
import { getErrorMessage } from '@/utils/getErrorMessage';
import React, { useState } from 'react';

interface UseChangePasswordOptions {
  onSuccess?: () => void;
}

interface UseChangePasswordResult {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  displayError: string | null;
  isSubmitting: boolean;
  setOldPassword: React.Dispatch<React.SetStateAction<string>>;
  setNewPassword: React.Dispatch<React.SetStateAction<string>>;
  setConfirmPassword: React.Dispatch<React.SetStateAction<string>>;
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

export const useChangePassword = ({
  onSuccess,
}: UseChangePasswordOptions = {}): UseChangePasswordResult => {
  const { completePasswordChange, error, isLoading } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError(null);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setLocalError('All password fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setLocalError('New password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setLocalError('New password fields must match');
      return;
    }

    try {
      await completePasswordChange(oldPassword, newPassword);
      onSuccess?.();
    } catch (submitError) {
      setLocalError(getErrorMessage(submitError, 'Failed to change password'));
    }
  };

  return {
    oldPassword,
    newPassword,
    confirmPassword,
    displayError: localError || error,
    isSubmitting: isLoading,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    handleSubmit,
  };
};

export default useChangePassword;
